import defineService from './defineService'
import { objectKeysToCamelCase } from './common';

const basket = defineService('get', 'api/v1/cart/basket/');
export const getBasketLinesCount = defineService('get', 'api/v1/cart/basket/lines/count/');
const product = defineService('get', 'api/v1/cart/products/:id/',
  {alternatives: {
    rounded: '-rounded' // rounded product
  }}
)
const basketLines = defineService('get', 'api/v1/cart/baskets/:id/lines/', {mainKey: 'results'})
const orderLines = defineService('get', 'api/v1/cart/orders/:id/lines/', {mainKey: 'results'})
const addProduct = defineService('post', 'api/v1/cart/basket/add-product/')
const putLine = defineService('put', 'api/v1/cart/baskets/:basket/lines/:line/')
const deleteLine = defineService('delete', 'api/v1/cart/baskets/:basket/lines/:line/')
const putLineAttribute = defineService('put', 'api/v1/cart/baskets/:basket/lines/:line/lineattributes/:id/');
const postVoucher = defineService('post', 'api/v1/cart/custom/basket/add-voucher/', {
  suffixFunc() { return '-406' }
});

/**
 * Get the current users basket. Expand the lines.
 */
export async function getBasket() {
  let result = await basket();
  let updatedLines = [];
  for (let line of result.lines) {
    expandAttrs(line);
    updatedLines.push({ ...line });
  }
  result.lines = updatedLines;
  return result
}

export async function expandLines(basket, lines=basketLines) {
  await expand(basket, {lines})
  for (var lineObj of basket.lines) {
    await expandLine(lineObj);
  }
  return basket;
}

async function expandLine(lineObj) {
  await expand(lineObj, {product});
  expandAttrs(lineObj);
  ensureLineHasId(lineObj);
}


export async function expandOrderLines(order) {
  if (order.id) {
    // The presence of `id` indicates that `order` already has `lines` data.
    // And we only need to update `lines` with copied attributes.
    return extendOrderLinesKeys(order);
  }
  ensureHasId(order);
  urlToId(order, 'owner');
  return expandLines(order, orderLines);
}

/**
 * Copy existing attrs to "camel cased" attrs for each order's line.
 * Attrs for copying indicated in included `keyArray`.
 *
 * @param {Object} order
 * @returns {Object} Updated order.
 */
export function extendOrderLinesKeys(order) {
  const keyArray = ['box_size', 'product_title'];
  let updatedLines = [];
  for (let line of order.lines) {
    updatedLines.push(objectKeysToCamelCase(line, keyArray));
  }
  order.lines = updatedLines;
  return order
}

async function expand(obj, attrs) {
  for (var attr in attrs) {
    if (typeof obj[attr] === 'string') {
      try{
        obj[attr] = await attrs[attr](obj[attr]);
      }catch(e){
        // TODO when does this happen
        console.error(`Error expanding ${attr}`, e);
        throw e;
      }
    }
  }
}

export const ATTRS = {
  boxSize: {type: Number, url: "https://www.brandly.com/services/cart/api/v1/cart/options/3/"},
  designId: {type: Number, url: "https://www.brandly.com/services/cart/api/v1/cart/options/1/"},
}

/**
 * Get the value for a particular attribute
 * @param {*} line
 * @param {*} attr_url -- eg ATTRS.BOX_SIZE
 */
export function getLineAttr(line, {url, type}) {
  // The urls won't match directly due to different environments;
  // just match the numeric portion
  let attr = findAttr(line.attributes, url);
  return attr && type(attr.value);
}

export function findAttr(attributes, attrUrl) {
  let idFromUrl = (url) => url.match(/\/\d+\//)[0];
  let attrId = idFromUrl(attrUrl);
  return attributes.find((obj) => idFromUrl(obj.option) === attrId);
}

function expandAttrs(line) {
  for (var key in ATTRS) {
    line[key] = getLineAttr(line, ATTRS[key]);
  }
}

/**
 * Ensure line object has an ID, extract from the url if needed.
 * @param {*} line
 */
function ensureLineHasId(line) {
  ensureHasId(line, /lines\/(\d+)\//);
}

/**
 * Ensure object has an ID, extract from the url if needed.
 * @param {*} object
 */
function ensureHasId(obj, re=/\/(\d+)\//) {
  if (obj.id) return;
  let match = re.exec(obj.url);
  console.assert(match[1]);
  obj.id = +match[1];
}

function urlToId(obj, attr, re=/\/(\d+)\//) {
  let value = obj[attr];
  if (!value || typeof value !== 'string') return;
  let m = re.exec(value);
  if (m) obj[attr] = +m[1];
}


/**
 *
 * @param {*} productUrl
 * @param {*} quantity
 * @param {*} designId
 * @param {*} boxSize
 * @param {number} [orderedFor] - `id` of team user
 */
export async function addToBasket(productUrl, quantity, designId, boxSize, orderedFor) {
  var payload = {
    url: productUrl,
    quantity,
    options: [
      {
        option: ATTRS.designId.url,
        value: designId
      },
      {
        option: ATTRS.boxSize.url,
        value: boxSize
      }
    ]
  };
  if (orderedFor) {
    payload.ordered_for = orderedFor;
  }
  var basket = await addProduct(payload);
  await expandLines(basket);
  return basket;
}

export async function updateLineItem(lineItemUrl, updates) {
  let lineObj = await putLine(lineItemUrl, updates);
  await expandLine(lineObj);
  for (var attrKey in ATTRS) {
    if (updates[attrKey]) {
      await updateLineItemAttr(lineObj, attrKey, updates[attrKey]);
    }
  }
  return lineObj;
}

export async function updateLineItemAttr(lineItem, attrKey, value) {
  let attrObj = findAttr(lineItem.attributes, ATTRS[attrKey].url);
  let line = lineItem.url;
  // Hack, the rest api expects a different way of referencing the line item.
  line = line.replace(/\/baskets\/\d+\//, '/');
  await putLineAttribute(attrObj.url, {...attrObj, line, value});
  // Ensure the in-memory structure is updated
  attrObj.value = value;
  lineItem[attrKey] = value;
}

export async function removeFromBasket(lineItemUrl) {
  return deleteLine(lineItemUrl);
}

export async function addVoucher(vouchercode) {
  return postVoucher({vouchercode});
}



// WEBPACK FOOTER //
// ./src/services/basket.js